import React, { useRef, useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
  LineChart as MuiLineChart,
  areaElementClasses,
  lineElementClasses,
} from "@mui/x-charts/LineChart";
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine";
import { useDrawingArea, useXScale, useYScale } from "@mui/x-charts/hooks";
import { formatChartTitle } from "../../utils";

interface LineChartData {
  [key: string]: string | number | null | undefined;
}

interface LineSeries {
  dataKey: string;
  label: string;
  color?: string;
  area?: boolean;
  stack?: string;
  dashed?: boolean;
}

interface LineChartProps {
  data: LineChartData[];
  xAxisKey?: string;
  series: LineSeries[];
  title?: string;
  height?: number;
  yAxisMin?: number;
  yAxisMax?: number;
  showMinMaxLines?: boolean;
  referenceMin?: number;
  referenceMax?: number;
}

function calculateTickStep(min: number, max: number): number {
  const range = max - min;
  if (range <= 0) return 1;
  const rawStep = range / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  let niceStep: number;
  if (normalized <= 1) niceStep = 1;
  else if (normalized <= 2) niceStep = 2;
  else if (normalized <= 5) niceStep = 5;
  else niceStep = 10;
  return niceStep * magnitude;
}

const AreaGradient: React.FC<{ id: string; color: string }> = ({ id, color }) => (
  <defs>
    <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
      <stop offset="0%" stopColor={color} stopOpacity={0.3} />
      <stop offset="100%" stopColor={color} stopOpacity={0} />
    </linearGradient>
  </defs>
);

const ChartOverlay: React.FC<{
  data: LineChartData[];
  series: LineSeries[];
  seriesWithSd: Set<string>;
  seriesWithMinMax: Set<string>;
  xAxisKey: string;
  clipId: string;
}> = ({ data, series, seriesWithSd, seriesWithMinMax, xAxisKey, clipId }) => {
  const { left, top, width, height } = useDrawingArea();
  const xScale = useXScale();
  const yScale = useYScale();

  const toXY = (xVal: unknown, yVal: number): [number, number] | null => {
    const px = (xScale as (v: unknown) => number | undefined)(xVal);
    const py = (yScale as (v: number) => number | undefined)(yVal);
    if (px == null || py == null) return null;
    return [px, py];
  };

  return (
    <>
      <defs>
        <clipPath id={clipId}>
          <rect x={left} y={top} width={width} height={height} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {series.map((s) => {
          const color = s.color || "#000";
          const elements: React.ReactNode[] = [];
          if (seriesWithSd.has(s.dataKey)) {
            const upperPts: [number, number][] = [];
            const lowerPts: [number, number][] = [];
            data.forEach((point) => {
              const xVal = point[xAxisKey];
              const val = typeof point[s.dataKey] === "number" ? (point[s.dataKey] as number) : null;
              const sd = typeof point[`${s.dataKey}_sd`] === "number" ? (point[`${s.dataKey}_sd`] as number) : null;
              if (val === null || sd === null || sd <= 0 || xVal == null) return;
              const upper = toXY(xVal, val + sd);
              const lower = toXY(xVal, Math.max(0, val - sd));
              if (upper) upperPts.push(upper);
              if (lower) lowerPts.push(lower);
            });
            if (upperPts.length >= 2) {
              const d = [
                `M ${upperPts[0][0]} ${upperPts[0][1]}`,
                ...upperPts.slice(1).map(([x, y]) => `L ${x} ${y}`),
                ...lowerPts.slice().reverse().map(([x, y]) => `L ${x} ${y}`),
                "Z",
              ].join(" ");
              elements.push(
                <path key={`sd-band-${s.dataKey}`} d={d} fill={color} fillOpacity={0.15} stroke="none" />
              );
            }
          }
          if (seriesWithMinMax.has(s.dataKey)) {
            const minPts: [number, number][] = [];
            const maxPts: [number, number][] = [];
            data.forEach((point) => {
              const xVal = point[xAxisKey];
              if (xVal == null) return;
              const minVal = typeof point[`${s.dataKey}_min`] === "number" ? (point[`${s.dataKey}_min`] as number) : null;
              const maxVal = typeof point[`${s.dataKey}_max`] === "number" ? (point[`${s.dataKey}_max`] as number) : null;
              if (minVal !== null) { const pt = toXY(xVal, minVal); if (pt) minPts.push(pt); }
              if (maxVal !== null) { const pt = toXY(xVal, maxVal); if (pt) maxPts.push(pt); }
            });
            if (minPts.length >= 2) {
              elements.push(
                <polyline key={`min-line-${s.dataKey}`} points={minPts.map(([x, y]) => `${x},${y}`).join(" ")} stroke={color} strokeWidth={1.5} strokeDasharray="5,3" strokeOpacity={0.6} fill="none" />
              );
            }
            if (maxPts.length >= 2) {
              elements.push(
                <polyline key={`max-line-${s.dataKey}`} points={maxPts.map(([x, y]) => `${x},${y}`).join(" ")} stroke={color} strokeWidth={1.5} strokeDasharray="5,3" strokeOpacity={0.6} fill="none" />
              );
            }
          }
          return elements.length > 0 ? <g key={s.dataKey}>{elements}</g> : null;
        })}
      </g>
    </>
  );
};

let chartInstanceCounter = 0;

export const LineChart: React.FC<LineChartProps> = ({
  data, xAxisKey, series, title, height = 300,
  yAxisMin, yAxisMax, showMinMaxLines = false, referenceMin, referenceMax,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const formattedTitle = formatChartTitle(title);

  const seriesWithSd = useMemo(() => {
    if (!showMinMaxLines || data.length === 0) return new Set<string>();
    const result = new Set<string>();
    for (const s of series) {
      if (data.some((d) => d[`${s.dataKey}_sd`] !== null && d[`${s.dataKey}_sd`] !== undefined)) result.add(s.dataKey);
    }
    return result;
  }, [showMinMaxLines, data, series]);

  const seriesWithMinMax = useMemo(() => {
    if (!showMinMaxLines || data.length === 0) return new Set<string>();
    const result = new Set<string>();
    for (const s of series) {
      if (data.some((d) => (d[`${s.dataKey}_min`] !== null && d[`${s.dataKey}_min`] !== undefined) || (d[`${s.dataKey}_max`] !== null && d[`${s.dataKey}_max`] !== undefined)))
        result.add(s.dataKey);
    }
    return result;
  }, [showMinMaxLines, data, series]);

  const detectedXAxisKey = useMemo(() => {
    if (xAxisKey) return xAxisKey;
    if (!data || data.length === 0) return "x";
    const firstItem = data[0];
    const seriesKeys = new Set(series.map((s) => s.dataKey));
    for (const key of ["label", "period", "day", "date", "xAxis", "x"]) {
      if (firstItem[key] !== undefined && !seriesKeys.has(key)) return key;
    }
    const nonSeriesKey = Object.keys(firstItem).find((key) => !seriesKeys.has(key));
    return nonSeriesKey || "x";
  }, [data, series, xAxisKey]);

  const chartIdRef = useRef<string>(`line-chart-${Date.now()}-${++chartInstanceCounter}-${Math.random().toString(36).substring(2, 9)}`);
  const chartId = chartIdRef.current;

  const { dataMin, dataMax } = useMemo(() => {
    let min = Infinity, max = -Infinity;
    for (const item of data) {
      for (const s of series) {
        const val = item[s.dataKey];
        if (typeof val === "number" && isFinite(val)) {
          if (val < min) min = val;
          if (val > max) max = val;
        }
        if (seriesWithSd.has(s.dataKey)) {
          const sd = typeof item[`${s.dataKey}_sd`] === "number" ? (item[`${s.dataKey}_sd`] as number) : null;
          if (sd !== null && typeof val === "number") {
            if (Math.max(0, val - sd) < min) min = Math.max(0, val - sd);
            if (val + sd > max) max = val + sd;
          }
        }
        if (seriesWithMinMax.has(s.dataKey)) {
          const minV = typeof item[`${s.dataKey}_min`] === "number" ? (item[`${s.dataKey}_min`] as number) : null;
          const maxV = typeof item[`${s.dataKey}_max`] === "number" ? (item[`${s.dataKey}_max`] as number) : null;
          if (minV !== null && minV < min) min = minV;
          if (maxV !== null && maxV > max) max = maxV;
        }
      }
    }
    return { dataMin: min === Infinity ? 0 : min, dataMax: max === -Infinity ? 100 : max };
  }, [data, series, seriesWithSd]);

  const effectiveYMin = useMemo(() => {
    const range = dataMax - dataMin;
    const padding = Math.max(range * 0.05, 1);
    let base = yAxisMin !== undefined ? yAxisMin : Math.max(0, Math.floor(dataMin - padding));
    if (showMinMaxLines && seriesWithSd.size === 0 && seriesWithMinMax.size === 0 && referenceMin !== undefined)
      base = Math.min(base, Math.floor(referenceMin - padding));
    return base;
  }, [yAxisMin, dataMin, dataMax, showMinMaxLines, referenceMin, seriesWithSd, seriesWithMinMax]);

  const effectiveYMax = useMemo(() => {
    const range = dataMax - dataMin;
    const padding = Math.max(range * 0.05, 1);
    let base = yAxisMax !== undefined ? yAxisMax : Math.ceil(dataMax + padding);
    if (showMinMaxLines && seriesWithSd.size === 0 && seriesWithMinMax.size === 0) {
      const refMaxValue = referenceMax !== undefined ? referenceMax : dataMax;
      base = Math.max(base, Math.ceil(refMaxValue + padding));
    }
    return base;
  }, [yAxisMax, dataMin, dataMax, showMinMaxLines, referenceMax, seriesWithSd, seriesWithMinMax]);

  const tickMinStep = useMemo(() => calculateTickStep(effectiveYMin, effectiveYMax), [effectiveYMin, effectiveYMax]);

  const areaSeriesIndices = useMemo(() => series.map((s, i) => (s.area ? i : null)).filter((i): i is number => i !== null), [series]);

  const muiSeries = useMemo(() => series.map((s, index) => ({
    id: `series-${chartId}-${index}`,
    dataKey: s.dataKey,
    label: s.label,
    color: s.color || theme.palette.primary.main,
    area: s.area || false,
    stack: s.stack,
    connectNulls: true,
  })), [series, theme.palette.primary.main, chartId]);

  const dashedSeriesIds = useMemo(() => series.map((s, i) => (s.dashed ? `series-${chartId}-${i}` : null)).filter((id): id is string => id !== null), [series, chartId]);
  const areaSeriesIds = useMemo(() => series.map((s, i) => (s.area ? `series-${chartId}-${i}` : null)).filter((id): id is string => id !== null), [series, chartId]);

  const chartSx = useMemo(() => {
    const sx: Record<string, unknown> = {};
    dashedSeriesIds.forEach((seriesId) => {
      sx[`& .${lineElementClasses.root}[data-series-id="${seriesId}"]`] = { strokeDasharray: "5 5" };
      sx[`& .${lineElementClasses.root}[data-series="${seriesId}"]`] = { strokeDasharray: "5 5" };
    });
    areaSeriesIds.forEach((seriesId) => {
      const seriesIndex = series.findIndex((_, idx) => `series-${chartId}-${idx}` === seriesId);
      const gradientId = `area-gradient-${chartId}-${seriesIndex}`;
      sx[`& .${areaElementClasses.root}[data-series-id="${seriesId}"]`] = { fill: `url(#${gradientId})` };
      sx[`& .${areaElementClasses.root}[data-series="${seriesId}"]`] = { fill: `url(#${gradientId})` };
    });
    return sx;
  }, [dashedSeriesIds, areaSeriesIds, series, chartId]);

  const getGradientIdForSeries = (seriesIndex: number) => `area-gradient-${chartId}-${seriesIndex}`;
  const refMin = referenceMin !== undefined ? referenceMin : dataMin;
  const refMax = referenceMax !== undefined ? referenceMax : dataMax;
  const minLabel = `Min: ${Math.round(refMin * 10) / 10}`;
  const maxLabel = `Max: ${Math.round(refMax * 10) / 10}`;

  return (
    <Box ref={containerRef} sx={{ width: "100%" }}>
      {formattedTitle && <Typography variant="h6" fontWeight={600} gutterBottom>{formattedTitle}</Typography>}
      <MuiLineChart
        dataset={data}
        height={height}
        xAxis={[{ scaleType: "point", dataKey: detectedXAxisKey, disableTicks: true }]}
        yAxis={[{ min: effectiveYMin, max: effectiveYMax, disableTicks: true, tickMinStep }]}
        series={muiSeries}
        margin={{ left: 0, right: 0, top: 20, bottom: 20 }}
        grid={{ vertical: true, horizontal: true }}
        sx={chartSx}
      >
        {areaSeriesIndices.map((seriesIndex) => {
          const seriesConfig = series[seriesIndex];
          const color = seriesConfig.color || theme.palette.primary.main;
          return <AreaGradient key={`gradient-${seriesIndex}`} id={getGradientIdForSeries(seriesIndex)} color={color} />;
        })}
        {(seriesWithSd.size > 0 || seriesWithMinMax.size > 0) && (
          <ChartOverlay data={data} series={series} seriesWithSd={seriesWithSd} seriesWithMinMax={seriesWithMinMax} xAxisKey={detectedXAxisKey} clipId={`overlay-clip-${chartId}`} />
        )}
        {showMinMaxLines && seriesWithSd.size === 0 && seriesWithMinMax.size === 0 && data.length > 0 && refMin !== refMax && (
          <>
            <ChartsReferenceLine y={refMax} label={maxLabel} labelAlign="end" lineStyle={{ stroke: theme.palette.text.secondary, strokeDasharray: "6 3", strokeWidth: 1.5, opacity: 0.7 }} labelStyle={{ fontSize: 11, fill: theme.palette.text.secondary }} />
            <ChartsReferenceLine y={refMin} label={minLabel} labelAlign="end" lineStyle={{ stroke: theme.palette.text.secondary, strokeDasharray: "6 3", strokeWidth: 1.5, opacity: 0.7 }} labelStyle={{ fontSize: 11, fill: theme.palette.text.secondary }} />
          </>
        )}
      </MuiLineChart>
    </Box>
  );
};
